import { useRef, useState } from 'react';
import { apiPost } from '@lib/apiClient';

/**
 * Optional lab voice note. Records on-device with MediaRecorder, uploads to /api/intake/voice (where
 * it is titled "Lab voice notes <member> <id>"), and reports the SharePoint paths up. Hidden entirely
 * when the device has no microphone / no MediaRecorder. Multiple notes are allowed (newest appended).
 */

function pickMime(): string {
  const cands = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  if (typeof MediaRecorder !== 'undefined') {
    for (const c of cands) if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return '';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result as string);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });
}

export function VoiceNote({
  campaign,
  kitBarcode,
  onChange,
}: {
  campaign: string;
  kitBarcode: string;
  onChange: (paths: string[]) => void;
}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clips, setClips] = useState<{ path: string; url: string }[]>([]);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const supported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof MediaRecorder !== 'undefined';

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const finish = async (mime: string) => {
    const blob = new Blob(chunksRef.current, { type: mime });
    if (!blob.size) return;
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await blobToDataUrl(blob);
      const res = await apiPost<{ voicePath: string }>('/api/intake/voice', {
        campaign,
        kitBarcode,
        audio: dataUrl,
        contentType: mime,
      });
      const voicePath = res.data.voicePath;
      if (!res.ok || typeof voicePath !== 'string') {
        setError((res.data.error as string) || `Upload failed (${res.status})`);
        return;
      }
      const next = [...clips, { path: voicePath, url: URL.createObjectURL(blob) }];
      setClips(next);
      onChange(next.map((c) => c.path));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the voice note');
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => void finish(rec.mimeType || mime || 'audio/webm');
      recRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Microphone unavailable');
      stopStream();
    }
  };

  const stop = () => {
    recRef.current?.stop();
    setRecording(false);
    stopStream();
  };

  if (!supported) return null;

  return (
    <div className="card">
      <div className="mb-2 flex items-center justify-between">
        <span className="field-label" style={{ marginBottom: 0 }}>
          Voice note <span style={{ color: 'var(--ink-300)' }}>(optional)</span>
        </span>
        {clips.length > 0 && (
          <span className="text-xs tab-num" style={{ color: 'var(--ink-400)' }}>
            {clips.length} saved
          </span>
        )}
      </div>
      {clips.map((c) => (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio key={c.path} src={c.url} controls className="mb-2 w-full" />
      ))}
      {!recording ? (
        <button
          type="button"
          onClick={() => void start()}
          disabled={busy}
          className="btn-bench btn-bench-ghost w-full disabled:opacity-50"
        >
          {busy ? 'Saving...' : clips.length ? 'Record another' : 'Record voice note'}
        </button>
      ) : (
        <button
          type="button"
          onClick={stop}
          className="btn-bench w-full"
          style={{ background: 'var(--crimson)', borderColor: 'var(--crimson)' }}
        >
          Stop recording
        </button>
      )}
      {error && <p className="mt-2 text-xs text-cdx-danger">{error}</p>}
    </div>
  );
}
